"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { ArrowLeft, CircleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AspectRatio } from "@/components/ui/aspect-ratio";


declare var wvcClient: any;

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 animate-pulse pt-8 pb-16">
        <div className="h-4 w-24 bg-muted rounded-md mb-10"></div>
        <div className="flex justify-center gap-2 mb-4">
          <div className="h-4 w-20 bg-muted rounded-md"></div>
        </div>
        <div className="h-12 w-4/5 bg-muted rounded-md mb-6 mx-auto"></div>
        <div className="flex justify-center gap-4 mb-10">
          <div className="h-6 w-32 bg-muted rounded-md"></div>
        </div>
        <div className="w-full aspect-[16/9] bg-muted rounded-lg mb-12"></div>
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-11/12 bg-muted rounded-md"></div>
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-4/5 bg-muted rounded-md"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-24 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-destructive/10 text-destructive mb-6">
          <CircleAlert className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold font-default mb-4">Unable to load legal insight</h2>
        <p className="text-muted-foreground mb-8">We encountered an error retrieving this article. Please return to our insights library.</p>
        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">
          <Link to="/HomePage">Return to Home</Link>
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-24 text-center">
        <h2 className="text-3xl font-bold font-default mb-4">Article Not Found</h2>
        <p className="text-muted-foreground mb-8">The legal insight you are looking for is no longer available or has been moved.</p>
        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">
          <Link to="/HomePage">Return to Home</Link>
        </Button>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-6 pt-8 pb-16 md:pb-24">
      <div className="mb-10">
        <Link 
          to="/HomePage" 
          className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Insights
        </Link>
      </div>

      <header className="mb-12 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="flex justify-center gap-2 mb-6 flex-wrap">
            {post.categories.map((cat, i) => (
              <Link 
                key={cat.id} 
                to={cat.link || "#"} 
                data-index={i}
                className="text-xs font-semibold uppercase tracking-widest text-secondary hover:text-secondary/80 transition-colors"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default leading-tight mb-8 text-primary">
          {post.title}
        </h1>
        
        <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground flex-wrap">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="h-7 w-7 border border-border">
                <AvatarImage src={post.author.avatar?.['24']} alt={post.author.name} />
                <AvatarFallback className="bg-muted text-primary">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          
          {post.author && post.date && <span className="opacity-50">•</span>}
          
          {post.date && (
            <time dateTime={post.date} className="font-medium">
              {new Date(post.date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </time>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-14 rounded-lg overflow-hidden shadow-lg border border-border/50">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="object-cover w-full h-full"
            />
          </AspectRatio>
        </div>
      )}

      <div
        className="font-default text-foreground/90 leading-relaxed text-lg md:text-xl [&>p]:mb-8 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mb-6 [&>h2]:mt-12 [&>h2]:font-default [&>h2]:text-primary [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mb-4 [&>h3]:mt-10 [&>h3]:font-default [&>h3]:text-primary [&>h4]:text-xl [&>h4]:font-semibold [&>h4]:mb-3 [&>h4]:mt-8 [&>h4]:font-default [&>h4]:text-primary [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-8 [&>ul>li]:mb-3 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-8 [&>ol>li]:mb-3 [&>blockquote]:border-l-4 [&>blockquote]:border-secondary/50 [&>blockquote]:pl-6 [&>blockquote]:py-2 [&>blockquote]:my-10 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:bg-accent/50 [&>blockquote]:rounded-r-lg [&>img]:rounded-lg [&>img]:my-12 [&>img]:shadow-md [&>img]:w-full [&>img]:object-cover [&>img]:border [&>img]:border-border/50 [&>figure]:my-12 [&>figure>img]:rounded-lg [&>figure>img]:shadow-md [&>figure>img]:w-full [&>figure>img]:object-cover [&>figure>img]:border [&>figure>img]:border-border/50 [&>figure>figcaption]:text-center [&>figure>figcaption]:text-sm [&>figure>figcaption]:text-muted-foreground [&>figure>figcaption]:mt-4 [&>a]:text-secondary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-secondary/80 transition-colors"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || '' }}
      />

      <Separator className="my-16 bg-border" />

      {post.author && (
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 bg-card p-8 md:p-10 rounded-lg border border-border shadow-sm">
          <Avatar className="h-24 w-24 border-4 border-background shadow-md shrink-0">
            <AvatarImage src={post.author.avatar?.['96']} alt={post.author.name} />
            <AvatarFallback className="text-3xl font-serif bg-primary text-primary-foreground">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-2xl font-bold font-default mb-2 text-primary">{post.author.name}</h3>
            <p className="text-muted-foreground text-base leading-relaxed mb-6 max-w-xl">
              Morrie I. Levine provides experienced counsel for South Florida clients navigating business ownership, real estate, estate planning, and probate matters.
            </p>
            {post.author.link && (
              <Button variant="outline" className="rounded-md px-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all" asChild>
                <Link to={post.author.link}>View all insights</Link>
              </Button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", per_page: 1 };
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="228145" id="single-post-template-section" className="bg-background text-foreground min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}